import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Plus, Edit, Trash2, Archive, X, AlertTriangle, Printer, UserMinus, Users, UserPlus, FileSpreadsheet, Database } from 'lucide-react';
import PermButton from './shared/PermButton';
import FloatingActions from './FloatingActions';
import { CopyIconButton, CopyAllButton, AddressActions, formatAddress } from './shared/CopyableLabel';
import { exportView, exportFullDump } from '../lib/exportXlsx';
import { useOnSavedViewsReset } from '../hooks/useSavedView';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { SortableHeader, useSort, ResetViewButton } from './ui/SortableHeader';
import ViewToggle, { readStoredView } from './shared/ViewToggle';
import EmployeeForm from './EmployeeForm';
import FileUpload from './FileUpload';
import AttachmentManager from './AttachmentManager';
import ContractsCrossRefPanel from './ContractsCrossRefPanel';
import RecordReportDialog from './RecordReportDialog';
import { getExpiringCredentials, EXPIRY_WINDOW_DAYS, formatDaysDelta } from '../utils/employeeExpiry';

import {
  EMPTY_EMPLOYEE_FORM, prepareSubmitData, populateFormFromEmployee,
} from './shared/employeeFormUtils';
import CandidatesPanel from './CandidatesPanel';
import usePermissions from '../hooks/usePermissions';
import useValueLabels from '../hooks/useValueLabels';
import useDeepShortcut from '../hooks/useDeepShortcut';
import useSubTabShortcut from '../hooks/useSubTabShortcut';
import { DateText } from './ui/week-badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


/**
 * Small amber badge shown next to an employee's name when they have
 * any credential expiring within `windowDays` (default 90). Hover to
 * see exactly which credentials and how many days until each lapses
 * (or how many days ago it lapsed).
 */
const ExpiringBadge = ({ employee, windowDays = EXPIRY_WINDOW_DAYS, className = '' }) => {
  const items = getExpiringCredentials(employee, windowDays);
  if (items.length === 0) return null;
  const tooltip = items.map(({ label, days }) => `${label}: ${formatDaysDelta(days)}`).join('\n');
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 border border-amber-200 ${className}`.trim()}
      title={tooltip}
      data-testid={`employee-expiring-badge-${employee.id}`}
    >
      <AlertTriangle className="w-3 h-3" />
      {items.length}
    </span>
  );
};


/**
 * Edit / Delete / Archive actions for a single employee row. Extracted
 * to top-level to keep React's reconciler stable — nested component
 * definitions cause the whole subtree to remount on every render.
 */
const EmployeeRowActions = ({
  employee,
  onMoveToCandidates,
  editDialogOpen,
  setEditDialogOpen,
  setEditingEmployee,
  setEmployeeForm,
  employeeForm,
  handleEdit,
  handleDelete,
  handleArchive,
  fetchData,
  onOpenReport,
}) => (
  <div className="flex flex-wrap justify-end gap-1 min-w-0">
    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8"
      title={i18n.t('auto.proj.printReport')}
      data-testid={`report-employee-${employee.id}`}
      onClick={(e) => { e.stopPropagation(); onOpenReport(employee); }}
    >
      <Printer className="w-4 h-4" />
    </Button>
    {onMoveToCandidates && (
      <Button
        variant="outline"
        size="icon"
      className="h-8 w-8"
        title={i18n.t('auto.proj.moveBackToCandidates')}
        data-testid={`employee-to-candidate-${employee.id}`}
        onClick={(e) => { e.stopPropagation(); onMoveToCandidates(employee); }}
      >
        <UserMinus className="w-4 h-4 text-amber-600" />
      </Button>
    )}
    <Dialog
      open={editDialogOpen === employee.id}
      onOpenChange={(open) => {
        if (!open) { setEditDialogOpen(null); setEditingEmployee(null); setEmployeeForm({ ...EMPTY_EMPLOYEE_FORM }); }
        else { setEditDialogOpen(employee.id); }
      }}
    >
      <DialogTrigger asChild>
        <PermButton
          perm="employees.edit"
          variant="outline"
          size="icon"
      className="h-8 w-8"
          data-testid={`edit-employee-${employee.id}`}
          onClick={() => {
            setEditingEmployee(employee);
            setEmployeeForm(populateFormFromEmployee(employee));
            setEditDialogOpen(employee.id);
          }}
        >
          <Edit className="w-4 h-4" />
        </PermButton>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] sm:max-w-[1400px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Employee - {employee.name || employee.employee_code}</DialogTitle>
          <DialogDescription>{i18n.t('auto.proj.updateEmployeeInformationAvatarAndAttachments')}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="details" className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1 w-full">
            <TabsTrigger value="details" data-testid="tab-details">{i18n.t('auto.proj.employeeDetails')}</TabsTrigger>
            <TabsTrigger value="avatar" data-testid="tab-avatar">{i18n.t('auto.proj.avatar')}</TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments">{i18n.t('auto.proj.attachments')}</TabsTrigger>
            <TabsTrigger value="contracts" data-testid={`employee-contracts-tab-${employee.id}`}>Documents</TabsTrigger>
          </TabsList>
          <TabsContent value="details">
            <EmployeeForm employeeForm={employeeForm} setEmployeeForm={setEmployeeForm} onSubmit={handleEdit} isEditing={true} />
          </TabsContent>
          <TabsContent value="avatar" className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-medium mb-4">{i18n.t('auto.proj.employeeAvatar')}</h3>
              <FileUpload type="avatar" entityId={employee.id} currentFile={employee.avatar_file}
                onUploadComplete={() => { toast.success(i18n.t('auto.proj.avatarUpdated')); fetchData(); }}
                onUploadError={() => toast.error(i18n.t('auto.proj.failedToUploadAvatar'))} />
            </div>
          </TabsContent>
          <TabsContent value="attachments">
            <AttachmentManager entityType="employee" entityId={employee.id} entityName={employee.name || employee.employee_code} />
          </TabsContent>
          <TabsContent value="contracts" className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-slate-800">{i18n.t('auto.proj.linkedDocuments')}</h3>
              <p className="text-xs text-slate-500">{i18n.t('auto.proj.contractsAnnexesSupportAndClientLetters2')}</p>
            </div>
            <ContractsCrossRefPanel scope="employee" id={employee.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <PermButton perm="employees.edit" variant="outline" size="icon" className="h-8 w-8" title="Delete permanently" data-testid={`delete-employee-${employee.id}`}><Trash2 className="w-4 h-4 text-red-500" /></PermButton>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{i18n.t('auto.proj.deleteEmployee')}</AlertDialogTitle>
          <AlertDialogDescription>Are you sure you want to delete {employee.name || employee.employee_code}? This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{i18n.t('auto.proj.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleDelete(employee.id)}>{i18n.t('auto.proj.delete')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8"
      title={i18n.t('auto.proj.archiveEmployee')}
      data-testid={`archive-employee-${employee.id}`}
      onClick={() => handleArchive(employee.id, employee.name || employee.employee_code)}
    >
      <Archive className="w-4 h-4 text-orange-500" />
    </Button>
  </div>
);

const EmployeesTab = ({ employees, user, fetchData, onNavigateToISO, navContext }) => {
  const { t } = useTranslation();
  const [employeeForm, setEmployeeForm] = useState({ ...EMPTY_EMPLOYEE_FORM });
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [view, setView] = useState(() => readStoredView('view:employees', 'cards'));
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('name', 'asc', 'employees');
  // Print-report dialog (per-employee PDF/Excel with section picker).
  const [reportEmployee, setReportEmployee] = useState(null);
  const { can } = usePermissions();
  // Polish captions for department / position values (Settings → General).
  const vl = useValueLabels();
  const canCandidates = can('candidates.view');
  const canConvert = can('candidates.convert');
  const [subTab, setSubTab] = useState('employees');
  useSubTabShortcut({
    tab: 'employees', ctxKey: 'employeesSubTab', value: canCandidates ? subTab : null, setValue: setSubTab,
    labels: { employees: 'auto.proj.employees', candidates: 'auto.proj.candidates' },
    allowed: canCandidates ? ['employees', 'candidates'] : ['employees'],
    perms: (v) => (v === 'candidates' ? ['candidates.view'] : ['employees.view']),
  });

  // Moving keeps the same internal id, so timesheets/invoices that
  // reference this person survive the round trip.
  const handleMoveToCandidates = async (employee) => {
    if (!window.confirm(`Move ${employee.name} back to Candidates? Their card leaves the Employees list.`)) return;
    try {
      await axios.post(`${API}/employees/${employee.id}/to-candidate`);
      toast.success(`${employee.name} moved to Candidates`);
      fetchData();
      setSubTab('candidates');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not move this employee');
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  // Handle cross-module navigation context
  React.useEffect(() => {
    if (navContext?.searchEmployee) {
      setSearchFilter(navContext.searchEmployee);
    }
    if (navContext?.filterExpiring) {
      setExpiringOnly(true);
    }
    if (navContext?.editEmployeeId) {
      const target = employees.find((e) => e.id === navContext.editEmployeeId);
      if (target) {
        setEditingEmployee(target);
        setEmployeeForm(populateFormFromEmployee(target));
        setEditDialogOpen(target.id);
      }
    }
    // We intentionally only react to navContext (not employees) so a
    // subsequent list refresh doesn't reopen the dialog after the
    // user has closed it.
  }, [navContext]);

  /**
   * Row/card click handler. Opens the edit dialog for the given
   * employee — but bails out if the click originated inside an
   * interactive element (buttons, links, avatar, dialog content) so
   * the row action buttons keep working normally.
   */
  // Dashboard shortcuts: pin the employee record the user has open.
  useDeepShortcut({
    active: !!editDialogOpen && !!editingEmployee,
    tab: 'employees',
    context: { editEmployeeId: editDialogOpen },
    label: editingEmployee?.name || editingEmployee?.employee_code,
    subtitle: t('nav.employees'),
    icon: 'employees',
    perms: ['employees.view'],
    priority: 3,
  });

  const openEmployeeFromRow = (employee, event) => {    if (!isAdmin) return;
    const target = event?.target;
    if (target && target.closest?.('button, a, [role="button"], [role="dialog"], [data-row-noopen]')) {
      return;
    }
    setEditingEmployee(employee);
    setEmployeeForm(populateFormFromEmployee(employee));
    setEditDialogOpen(employee.id);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/employees`, prepareSubmitData(employeeForm));
      toast.success(t('employees.toasts.created'));
      setEmployeeDialogOpen(false);
      setEmployeeForm({ ...EMPTY_EMPLOYEE_FORM });
      fetchData();
    } catch (err) { toast.error(t('auto.proj.errorCreatingEmployee') + (err.response?.data?.detail || err.message)); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${API}/employees/${editingEmployee.id}`, prepareSubmitData(employeeForm));
      toast.success(t('employees.toasts.updated'));
      setEditingEmployee(null);
      setEditDialogOpen(null);
      setEmployeeForm({ ...EMPTY_EMPLOYEE_FORM });
      fetchData();
    } catch (err) { toast.error(t('auto.proj.errorUpdatingEmployee') + (err.response?.data?.detail || err.message)); }
  };

  const handleDelete = async (id, force = false) => {
    try {
      await axios.delete(`${API}/employees/${id}${force ? '?force=true' : ''}`);
      toast.success(t('employees.toasts.deleted'));
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      if (msg.includes('Please remove') || msg.includes('Cannot delete employee')) {
        if (window.confirm(`${msg}\n\nForce delete all associated data? This cannot be undone.`)) {
          return handleDelete(id, true);
        }
      } else { toast.error(t('auto.proj.errorDeletingEmployee') + msg); }
    }
  };

  const handleArchive = async (id, name) => {
    if (!window.confirm(`Archive ${name}? They will be hidden but can be restored from Settings > Archived.`)) return;
    try {
      await axios.put(`${API}/employees/${id}/archive`);
      toast.success(`${name} archived`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error archiving employee'); }
  };

  // Distinct department / position values seen in the data (for filter dropdowns)
  const departments = useMemo(() => {
    const s = new Set();
    employees.forEach(e => { if (e.department) s.add(e.department); });
    return Array.from(s).sort();
  }, [employees]);

  const positions = useMemo(() => {
    const s = new Set();
    employees.forEach(e => {
      const p = e.position_1 || e.position;
      if (p) s.add(p);
    });
    return Array.from(s).sort();
  }, [employees]);

  const filtered = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    return employees.filter(e => {
      if (q) {
        const hay = [e.name, e.first_name, e.last_name, e.email, e.phone, e.employee_code, e.position, e.position_1, e.department, e.country]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && (e.status || 'active') !== statusFilter) return false;
      if (departmentFilter !== 'all' && (e.department || '') !== departmentFilter) return false;
      if (positionFilter !== 'all' && (e.position_1 || e.position || '') !== positionFilter) return false;
      if (expiringOnly && getExpiringCredentials(e, EXPIRY_WINDOW_DAYS).length === 0) return false;
      return true;
    });
  }, [employees, searchFilter, statusFilter, departmentFilter, positionFilter, expiringOnly]);

  const filtersActive =
    !!searchFilter || statusFilter !== 'all' || departmentFilter !== 'all' || positionFilter !== 'all' || expiringOnly;
  const sortActive = sortKey !== 'name' || sortDir !== 'asc';
  const resetAll = () => {
    setSearchFilter('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setPositionFilter('all');
    setExpiringOnly(false);
    resetSort();
  };

  useOnSavedViewsReset(resetAll);

  const getSortValue = (e, key) => {
    switch (key) {
      case 'name': return (e.name || e.employee_code || '').toLowerCase();
      case 'department': return (e.department || '').toLowerCase();
      case 'position': return (e.position_1 || e.position || '').toLowerCase();
      case 'country': return (e.country || '').toLowerCase();
      case 'start_date': return e.start_date ? new Date(e.start_date).getTime() : 0;
      case 'status': return e.status || '';
      default: return '';
    }
  };
  const sorted = useMemo(
    () => [...filtered].sort(comparator(getSortValue)),
    [filtered, comparator]
  );

  const employeesView = (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t('employees.title')}</h2>
          <p className="text-slate-600">{t('auto.proj.manageYourTeamMembersAndTheir')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
          <ViewToggle storageKey="view:employees" value={view} onChange={setView} />
          {isAdmin && (
            <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
              <DialogTrigger asChild>
                <PermButton perm="employees.edit" className="flex items-center gap-2 shrink-0" data-testid="add-employee-btn"><Plus className="w-4 h-4" /><span>{t('employees.addEmployee')}</span></PermButton>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-[1400px] max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('employees.addEmployee')}</DialogTitle>
                  <DialogDescription>{t('auto.proj.createAComprehensiveEmployeeRecord')}</DialogDescription>
                </DialogHeader>
                <EmployeeForm employeeForm={employeeForm} setEmployeeForm={setEmployeeForm} onSubmit={handleCreate} isEditing={false} />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
          <Input
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder={t('employees.searchPlaceholder')}
            className="pr-8"
            data-testid="employee-search-input"
          />
          {searchFilter && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              onClick={() => setSearchFilter('')}
              data-testid="employee-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="employee-status-filter"><SelectValue placeholder={t('common.status')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="active">{t('employees.status.active')}</SelectItem>
            <SelectItem value="inactive">{t('employees.status.not_active')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="w-[180px]" data-testid="employee-department-filter"><SelectValue placeholder={t('common.department')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{vl(d)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={positionFilter} onValueChange={setPositionFilter}>
          <SelectTrigger className="w-[200px]" data-testid="employee-position-filter"><SelectValue placeholder={t('common.position')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {positions.map(p => <SelectItem key={p} value={p}>{vl(p)}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Compliance quick-filter: employees whose any credential
            (A1, OSHA, passport, visas, driving licence, etc.) is
            about to lapse — including already-expired ones. */}
        <Button
          type="button"
          variant={expiringOnly ? 'default' : 'outline'}
          size="sm"
          className={`h-9 ${expiringOnly ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}`}
          onClick={() => setExpiringOnly(v => !v)}
          data-testid="employee-expiring-filter"
          title={`Employees with any credential expiring within ${EXPIRY_WINDOW_DAYS} days (or already expired)`}
        >
          <AlertTriangle className="w-4 h-4 mr-1.5" />
          Expiring in {EXPIRY_WINDOW_DAYS}d
        </Button>
        <ResetViewButton
          show={filtersActive || sortActive}
          onReset={resetAll}
          testId="employees-reset-view-btn"
        />
        <div className="text-xs text-slate-500 ml-auto" data-testid="employees-result-count">
          {sorted.length} of {employees.length}
        </div>
      </div>

      {view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sorted.map((employee) => (
            <Card
              key={employee.id}
              className={`bg-white border-0 shadow-sm ${isAdmin ? 'cursor-pointer hover:ring-2 hover:ring-blue-100 transition-shadow' : ''}`}
              data-testid={`employee-card-${employee.id}`}
              onClick={(e) => openEmployeeFromRow(employee, e)}
            >
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-start gap-3 min-w-0 w-full">
                      <Avatar
                        className="h-12 w-12 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all shrink-0"
                        onClick={(ev) => { ev.stopPropagation(); onNavigateToISO?.(employee); }}
                        title={t('auto.proj.viewIsoRecordsForThisEmployee')}
                        data-testid={`employee-avatar-${employee.id}`}
                        data-row-noopen
                      >
                        <AvatarImage
                          src={employee.avatar_file ? `${BACKEND_URL}/api/files/avatar/${employee.avatar_file}` : undefined}
                          alt={employee.name || employee.employee_code}
                        />
                        <AvatarFallback className="bg-slate-200 text-slate-600">
                          {(employee.name || employee.employee_code)?.charAt(0)?.toUpperCase() || 'E'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900 break-words flex items-center gap-1.5 flex-wrap">
                          {employee.name || employee.employee_code}
                          <ExpiringBadge employee={employee} />
                          <CopyAllButton testId={`copy-all-employee-${employee.id}`} title={employee.name}
                            lines={[
                              ['Name', employee.name],
                              ['Employee code', employee.employee_code],
                              ['Position', employee.position_1 || employee.position],
                              ['Department', employee.department],
                              ['Email', employee.email],
                              ['Phone', employee.phone],
                            ]} />
                        </p>
                        {(employee.email || employee.phone) && (
                          <p className="text-xs text-slate-500 break-all flex items-center gap-1 flex-wrap">
                            {employee.email}
                            <CopyIconButton label="Email" value={employee.email} testId={`copy-employee-email-${employee.id}`} />
                            {employee.phone && <span className="text-slate-400">·</span>}
                            {employee.phone}
                            <CopyIconButton label="Phone" value={employee.phone} testId={`copy-employee-phone-${employee.id}`} />
                          </p>
                        )}
                        <p className="text-sm text-slate-600 break-words">{vl(employee.position_1 || employee.position)}{employee.department ? ` · ${vl(employee.department)}` : ''}</p>
                        <Badge variant={employee.status === 'active' ? 'default' : 'secondary'} className="text-xs mt-1">
                          {employee.status === 'active' ? t('auto.proj.active') : t('auto.proj.notActive')}
                        </Badge>
                      </div>
                    </div>
                    {isAdmin && (
                      <EmployeeRowActions
                        employee={employee}
                        editDialogOpen={editDialogOpen}
                        setEditDialogOpen={setEditDialogOpen}
                        setEditingEmployee={setEditingEmployee}
                        setEmployeeForm={setEmployeeForm}
                        employeeForm={employeeForm}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleArchive={handleArchive}
                        fetchData={fetchData}
                        onOpenReport={setReportEmployee}
                        onMoveToCandidates={canConvert ? handleMoveToCandidates : null}
                      />
                    )}
                  </div>
                  <div><p className="text-sm font-medium text-slate-700">{t('auto.proj.email')}</p><p className="text-sm text-slate-600 break-all">{employee.email || 'Not provided'}</p></div>
                  {employee.url && (
                    <div><p className="text-sm font-medium text-slate-700">{t('auto.proj.webLink')}</p>
                      <a href={employee.url.startsWith('http') ? employee.url : `https://${employee.url}`} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate block" data-testid={`employee-url-${employee.id}`}>{employee.url}</a>
                    </div>
                  )}
                  <div><p className="text-sm font-medium text-slate-700">{t('auto.proj.phone')}</p><p className="text-sm text-slate-600 break-words">{employee.phone || 'Not provided'}</p></div>
                  <div><p className="text-sm font-medium text-slate-700">{t('auto.proj.department')}</p><p className="text-sm text-slate-600 break-words">{vl(employee.department)}</p></div>
                  <div><p className="text-sm font-medium text-slate-700">{t('auto.proj.country')}</p><p className="text-sm text-slate-600 break-words">{employee.country || 'Not provided'}</p></div>
                  {(employee.address || employee.country) && (
                    <div data-testid={`employee-address-${employee.id}`}>
                      <p className="text-sm font-medium text-slate-700">{t('auto.proj.address')}</p>
                      <p className="text-sm text-slate-600 break-words flex items-start gap-1">
                        <span className="break-words">{formatAddress([employee.address, employee.country])}</span>
                        <AddressActions parts={[employee.address, employee.country]}
                          testId={`copy-employee-address-${employee.id}`} />
                      </p>
                    </div>
                  )}
                  <div><p className="text-sm font-medium text-slate-700">{t('auto.proj.startDate2')}</p>
                    <p className="text-sm text-slate-600">{employee.start_date ? <DateText value={employee.start_date} /> : 'Not provided'}</p></div>
                  {employee.hourly_rate_1 > 0 && (
                    <div><p className="text-sm font-medium text-slate-700">{t('auto.proj.hourlyRate')}</p>
                      <p className="text-sm text-slate-600">
                        {employee.currency_1 === 'EUR' ? '€' : employee.currency_1 === 'USD' ? '$' : employee.currency_1 === 'PLN' ? 'zł' : employee.currency_1 || ''}{employee.hourly_rate_1}/hr
                      </p></div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white border-0 shadow-sm">
          <Table data-testid="employees-list-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[44px]"></TableHead>
                <SortableHeader column="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="employees-sort">{t('employees.columns.name')}</SortableHeader>
                <SortableHeader column="department" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="employees-sort" className="hidden md:table-cell">{t('employees.columns.department')}</SortableHeader>
                <SortableHeader column="position" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="employees-sort" className="hidden lg:table-cell">{t('employees.columns.position')}</SortableHeader>
                <SortableHeader column="country" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="employees-sort" className="hidden lg:table-cell">{t('auto.proj.country')}</SortableHeader>
                <SortableHeader column="start_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="employees-sort" className="hidden xl:table-cell">{t('auto.proj.startDate2')}</SortableHeader>
                <SortableHeader column="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="employees-sort" className="hidden sm:table-cell w-[110px]">{t('employees.columns.status')}</SortableHeader>
                <TableHead className="text-right w-[116px] sm:w-[156px] lg:w-[196px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((employee) => (
                <TableRow
                  key={employee.id}
                  data-testid={`employee-row-${employee.id}`}
                  className={isAdmin ? 'cursor-pointer hover:bg-slate-50' : undefined}
                  onClick={(e) => openEmployeeFromRow(employee, e)}
                >
                  <TableCell className="align-top">
                    <Avatar
                      className="h-8 w-8 cursor-pointer"
                      onClick={(ev) => { ev.stopPropagation(); onNavigateToISO?.(employee); }}
                      title={t('auto.proj.viewIsoRecordsForThisEmployee')}
                      data-row-noopen
                    >
                      <AvatarImage
                        src={employee.avatar_file ? `${BACKEND_URL}/api/files/avatar/${employee.avatar_file}` : undefined}
                        alt={employee.name || employee.employee_code}
                      />
                      <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                        {(employee.name || employee.employee_code)?.charAt(0)?.toUpperCase() || 'E'}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="font-medium text-slate-900 align-top break-words">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="break-words">{employee.name || employee.employee_code}</span>
                      <ExpiringBadge employee={employee} />
                    </div>
                    {/* The columns hidden on small screens fold in here instead. */}
                    <div className="md:hidden text-xs text-slate-500 break-words">
                      {[vl(employee.position_1 || employee.position), vl(employee.department)].filter(Boolean).join(' · ')}
                    </div>
                    <div className="lg:hidden text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                      {employee.country || ''}
                      <AddressActions parts={[employee.address, employee.country]}
                        testId={`copy-fold-employee-address-${employee.id}`} />
                    </div>
                    <div className="sm:hidden text-xs text-slate-500">
                      {employee.status === 'active' ? t('auto.proj.active') : t('auto.proj.notActive')}
                    </div>
                    {employee.email && (
                      <div className="text-xs text-slate-500 flex items-center gap-1 break-all">
                        {employee.email}
                        <CopyIconButton label="Email" value={employee.email} testId={`copy-row-employee-email-${employee.id}`} />
                      </div>
                    )}
                    {employee.phone && (
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        {employee.phone}
                        <CopyIconButton label="Phone" value={employee.phone} testId={`copy-row-employee-phone-${employee.id}`} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-600 hidden md:table-cell align-top break-words">{employee.department || '—'}</TableCell>
                  <TableCell className="text-slate-600 hidden lg:table-cell align-top break-words">{employee.position_1 || employee.position || '—'}</TableCell>
                  <TableCell className="text-slate-600 hidden lg:table-cell align-top break-words">
                    <span className="inline-flex items-start gap-1 flex-wrap">
                      {employee.country || '—'}
                      <AddressActions parts={[employee.address, employee.country]}
                        testId={`copy-row-employee-address-${employee.id}`} />
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600 hidden xl:table-cell align-top">{employee.start_date ? <DateText value={employee.start_date} /> : '—'}</TableCell>
                  <TableCell className="hidden sm:table-cell align-top">
                    <Badge variant={employee.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                      {employee.status === 'active' ? t('auto.proj.active') : t('auto.proj.notActive')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right align-top">
                    <div className="flex items-center justify-end gap-1 flex-wrap w-[112px] sm:w-[152px] lg:w-[188px] ml-auto">
                      <CopyAllButton testId={`copy-all-row-employee-${employee.id}`} title={employee.name}
                        lines={[
                          ['Name', employee.name],
                          ['Employee code', employee.employee_code],
                          ['Position', employee.position_1 || employee.position],
                          ['Department', employee.department],
                          ['Email', employee.email],
                          ['Phone', employee.phone],
                        ]} />
                      {isAdmin && (
                      <EmployeeRowActions
                        employee={employee}
                        editDialogOpen={editDialogOpen}
                        setEditDialogOpen={setEditDialogOpen}
                        setEditingEmployee={setEditingEmployee}
                        setEmployeeForm={setEmployeeForm}
                        employeeForm={employeeForm}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleArchive={handleArchive}
                        fetchData={fetchData}
                        onOpenReport={setReportEmployee}
                        onMoveToCandidates={canConvert ? handleMoveToCandidates : null}
                      />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500 text-sm">
                    {t('auto.proj.noEmployeesMatchTheCurrentFilters')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
      <RecordReportDialog
        open={!!reportEmployee}
        onOpenChange={(o) => !o && setReportEmployee(null)}
        kind="employee"
        recordId={reportEmployee?.id}
        recordName={reportEmployee?.name}
      />

      <FloatingActions actions={[
        { icon: Plus, label: t('employees.addEmployee'), tone: 'primary',
          testId: 'employees-floating-add-btn', hidden: !isAdmin,
          onClick: () => setEmployeeDialogOpen(true) },
        { icon: FileSpreadsheet, label: t('auto.lbl.exportThisViewToExcel'),
          testId: 'employees-floating-export-btn',
          onClick: () => exportView({
            filename: 'employees', sheetName: 'Employees', rows: sorted,
            columns: [
              { header: t('auto.lbl.name'), key: 'name' },
              { header: t('auto.lbl.employeeCode2'), key: 'employee_code' },
              { header: t('auto.lbl.email'), key: 'email' },
              { header: t('auto.lbl.phone'), key: 'phone' },
              { header: t('auto.lbl.department'), key: 'department' },
              { header: t('auto.lbl.position'), value: (e) => e.position_1 || e.position || '' },
              { header: t('auto.lbl.country'), key: 'country' },
              { header: t('auto.lbl.address'), value: (e) => formatAddress([e.address, e.country]) },
              { header: t('auto.lbl.startDate2'), key: 'start_date' },
              { header: t('auto.lbl.status'), key: 'status' },
            ],
          }) },
        { icon: Database, label: t('auto.lbl.exportAllEmployeesWithEveryField'),
          testId: 'employees-floating-export-all-btn', hidden: !isAdmin,
          onClick: () => exportFullDump({ filename: 'employees', sheetName: 'Employees', rows: employees }) },
      ]} />
    </div>
  );

  if (!canCandidates) return employeesView;

  return (
    <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
      <TabsList className="flex flex-wrap h-auto gap-1 p-1">
        <TabsTrigger value="employees" className="px-3 py-2" data-testid="employees-subtab">
          <Users className="w-4 h-4 mr-1.5" /> {t('auto.proj.employees')}
        </TabsTrigger>
        <TabsTrigger value="candidates" className="px-3 py-2" data-testid="candidates-subtab">
          <UserPlus className="w-4 h-4 mr-1.5" /> {t('auto.proj.candidates')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="employees">{employeesView}</TabsContent>
      <TabsContent value="candidates">
        <CandidatesPanel onMoved={fetchData} />
      </TabsContent>
    </Tabs>
  );

  /* EmployeeRowActions extracted to module scope above to keep React's
     reconciler stable — see the note at the top of this file. */
};

export default EmployeesTab;
